import * as React from 'react';
import classnames from 'classnames';
import { isNil, snakeCase } from 'lodash';

import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import Entity from 'components/entity/Entity';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';
import StatusLabel from 'components/status-label/StatusLabel';
import SVGIcon from 'components/svg-icon/SVGIcon';
import Popover from 'components/popover/Popover';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';
import MoreButton from 'components/buttons/more-button/MoreButton';

import { __t, applyTranslations } from 'utils/language';
import { CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE } from 'utils/channels';
import { getDisplayDate } from 'utils/date';
import { getFormattedFollowersValue, getNumFollowers } from 'utils/influencers';
import { getFormattedPercentageRate } from 'utils/formatters';
import { DELIVERABLE_TYPES } from 'utils/deliverables';

import type { AgencyInfluencersProfileOverview, InfluencersAgencyOverdueTask } from 'types';

applyTranslations(require('./influencers-agency-overview.i18n.json'));

import './influencers-agency-overview.scss';

type ContentItemType = 'goLive' | 'contentApproval' | 'insightsProvision';

const AVATAR_SIZE = {
  width: 24,
  height: 24
};

const typeMapping = {
  pending: 'warning',
  rejected: 'danger'
};

export const getCreatorColumn = () => ({
  title: __t('influencers_agency_overview.influencers_table.columns.creator'),
  key: 'creator',
  width: 248,
  render: (item: AgencyInfluencersProfileOverview) => {
    const { id, name, image, status } = item;
    const entityUrl = `/content-creator/profiles/${id ?? ''}`;

    return (
      <div>
        <Entity>
          <EntityAvatar
            bordered
            customSize={AVATAR_SIZE}
            entity={{
              image
            }}
            link={entityUrl}
            onClick={(event) => {
              event.stopPropagation();
            }}
            openInNewTab
          />
          <EntityName
            size="large"
            entity={{
              displayName: name
            }}
            link={entityUrl}
            onClick={(event) => {
              event.stopPropagation();
            }}
            openInNewTab
            hideTitle
          />
          {
            status === 'approved' && (
              <div className="influencers-agency-overview-table__verification-label-icon">
                <SVGDynamicIcon
                  name="shield-checkmark-inside-custom-bg-white-fg"
                  width={13}
                  height={16}
                  customColor="primary"
                />
              </div>
            )
          }
          {
            ['rejected', 'pending'].includes(status) && (
              <StatusLabel
                type={typeMapping[status]}
                size="nano"
                className="influencers-agency-overview-table__verification-label-icon"
                label={__t(`influencers_agency_overview.influencers_table.statuses.${status}`)}
              />
            )
          }
        </Entity>
      </div>
    );
  }
});

export const getRegistrationColumn = () => ({
  title: __t('influencers_agency_overview.influencers_table.columns.registration'),
  key: 'registration',
  width: 104,
  render: (item: AgencyInfluencersProfileOverview) => {
    const { registeredAt } = item;

    return (
      <div className="influencers-agency-overview-table__cell-rows">
        <div className="influencers-agency-overview-table__cell-row influencers-agency-overview-table__cell-row--centered">
          {getDisplayDate(registeredAt)}
        </div>
      </div>
    );
  }
});

export const getChannelColumn = () => ({
  title: __t('influencers_agency_overview.influencers_table.columns.channel'),
  key: 'channels',
  width: 76,
  render: (item: AgencyInfluencersProfileOverview) => (
    <div className="influencers-agency-overview-table__cell-rows">
      {
          [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE]
            .map(channelType => {
              const channel = item[channelType];

              if (!channel) {
                return null;
              }

              return (
                <div
                  className="influencers-agency-overview-table__cell-row influencers-agency-overview-table__cell-row--centered-vertical"
                  key={channelType}
                >
                  <div className="influencers-agency-overview-table__channel">
                    <Tooltip
                      message={__t(`influencers_agency_overview.influencers_table.tooltips.channel_${channelType}`)}
                    >
                      <div className="influencers-agency-overview-table__channel-icon">
                        <SVGIcon
                          name={`${channelType}-no-customisation`}
                          width={16}
                          height={16}
                          link={channel.channelUrl}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </Tooltip>
                  </div>
                </div>
              );
            })
        }
    </div>
  )
});

export const getFollowersColumn = () => ({
  title: (
    <div className="influencers-agency-overview-table__head-icon">
      <Tooltip
        message={__t('influencers_agency_overview.influencers_table.tooltips.followers')}
      >
        <SVGDynamicIcon
          name="users-connected-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: 44,
  key: 'followers',
  customizable: true,
  render: (item: AgencyInfluencersProfileOverview) => (
    <div className="influencers-agency-overview-table__cell-rows">
      {
          [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE]
            .map(channelType => {
              const channel = item[channelType];

              if (!channel) {
                return null;
              }

              const numFollowers = getNumFollowers(channel.followers);
              const formattedFollowers = getFormattedFollowersValue(numFollowers);
              const classes = classnames('influencers-agency-overview-table__cell-row', {
                'influencers-agency-overview-table__cell-row--centered': true
              });

              return (
                <div
                  className={classes}
                  key={channelType}
                >
                  <div className="influencers-agency-overview-table__metric">
                    {formattedFollowers}
                  </div>
                </div>
              );
            })
        }
    </div>
  )
});

export const getEngagementColumn = () => ({
  title: (
    <div className="influencers-agency-overview-table__head-icon">
      <Tooltip
        message={__t('influencers_agency_overview.influencers_table.tooltips.engagement')}
      >
        <SVGDynamicIcon
          name="rotation-arrows-custom-fg"
          width={16}
          height={16}
          customColor="grey"
        />
      </Tooltip>
    </div>
  ),
  width: 44,
  key: 'engagement_rate',
  customizable: true,
  render: (item: AgencyInfluencersProfileOverview) => (
    <div className="influencers-agency-overview-table__cell-rows">
      {
          [CHANNEL_TYPE_INSTAGRAM, CHANNEL_TYPE_TIKTOK, CHANNEL_TYPE_YOUTUBE]
            .map(channelType => {
              const channel = item[channelType];

              if (!channel) {
                return null;
              }

              const engagement = getFormattedPercentageRate(channel.engagementRate);
              const classes = classnames('influencers-agency-overview-table__cell-row', {
                'influencers-agency-overview-table__cell-row--centered': true
              });

              return (
                <div
                  className={classes}
                  key={channelType}
                >
                  <div className="influencers-agency-overview-table__metric">
                    {engagement}
                  </div>
                </div>
              );
            })
        }
    </div>
  )
});

export const getCampaignStatusColumn = () => ({
  title: __t('influencers_agency_overview.influencers_table.columns.campaign_status'),
  key: 'campaign_status',
  width: 224,
  render: (item: AgencyInfluencersProfileOverview) => {
    const { campaigns } = item;

    if (item.status !== 'approved') {
      return '';
    }

    return (
      <div className="influencers-agency-overview-table__campaign-status-row">
        <div className="influencers-agency-overview-table__campaign-status">
          {campaigns.offeredCampaignsCount ?
            `${campaigns.offeredCampaignsCount} pending offers` : '—'}
        </div>
        <div className="influencers-agency-overview-table__campaign-status">
          {campaigns.ongoingCampaignsCount ?
            `${campaigns.ongoingCampaignsCount} ongoing` : '—'}
        </div>
      </div>
    );
  }
});

const getOverdueTasksByCampaignId = (overdueTasks: Array<InfluencersAgencyOverdueTask>) => overdueTasks.reduce((prevValue, task) => {
  if (prevValue[task.campaignId]) {
    prevValue[task.campaignId].push(task);
  } else {
    // eslint-disable-next-line no-param-reassign
    prevValue[task.campaignId] = [task];
  }

  return prevValue;
}, {});

const renderContentItem = (
  deliverable: InfluencersAgencyOverdueTask,
  type: ContentItemType
): React.ReactNode => {
  const startDate = deliverable[type]?.start;
  const endDate = deliverable[type]?.deadline;

  if (isNil(startDate) && isNil(endDate)) {
    return null;
  }

  const displayStartDate = getDisplayDate(startDate);
  const displayEndDate = getDisplayDate(endDate);
  const datesAreEqual = displayStartDate === displayEndDate;

  const containerClasses = classnames('influencers-agency-overview-table__overdue-deliverables-popover-item', {
    'influencers-agency-overview-table__overdue-deliverables-popover-item--overdue': deliverable[type].isOverdue
  });

  return (
    <div className={containerClasses}>
      <div className="influencers-agency-overview-table__overdue-deliverables-popover-description">
        {__t(`influencers_agency_overview.influencers_table.overdue_popover.${snakeCase(type)}`)}
      </div>
      <div className="influencers-agency-overview-table__overdue-deliverables-popover-range">
        {`${displayStartDate}${!datesAreEqual ? ` - ${displayEndDate}` : ''}`}
      </div>
      {
        deliverable[type]?.isOverdue && (
          <div className="influencers-agency-overview-table__overdue-deliverables-popover-overdue">
            {__t('influencers_agency_overview.influencers_table.overdue_popover.overdue')}
          </div>
        )
      }
    </div>
  );
};

const renderContentDeliverable = (
  deliverable: InfluencersAgencyOverdueTask,
  counts: {
    [x: string]: number
  }): React.ReactNode => {
  const defaultDeliverableTitles = DELIVERABLE_TYPES.map(type => (
    type.replace(/_/, ' ').toLowerCase()
  ));

  const deliverableName = deliverable.deliverableName?.toLowerCase() || '';
  const index = defaultDeliverableTitles.includes(deliverableName)
    ? ((counts[deliverableName] ?? 0) + 1)
    : 0;

  // eslint-disable-next-line no-param-reassign
  counts[deliverableName] = index;

  return (
    <React.Fragment key={`overdue-deliverables-popover--${deliverable.deliverableId}`}>
      <div className="influencers-agency-overview-table__overdue-deliverables-popover-name">
        {`${deliverable.deliverableName} #${counts[deliverableName]}`}
      </div>
      <div className="influencers-agency-overview-table__overdue-deliverables-popover-items">
        {renderContentItem(deliverable, 'goLive')}
        {renderContentItem(deliverable, 'contentApproval')}
        {renderContentItem(deliverable, 'insightsProvision')}
      </div>
    </React.Fragment>
  );
};

export const getOverdueTasksColumn = () => ({
  title: __t('influencers_agency_overview.influencers_table.columns.overdue_tasks'),
  key: 'overdue_tasks',
  width: 128,
  render: (item: AgencyInfluencersProfileOverview) => {
    const { overdueTasks } = item;

    if (!overdueTasks.length) {
      return null;
    }

    const content = (
      <div className="influencers-agency-overview-table__banner influencers-agency-overview-table__overdue">
        <div className="influencers-agency-overview-table__icon">
          <SVGDynamicIcon
            name="exclamation-inside-circle-white-bg-custom-fg"
            width={12}
            height={12}
            customColor="red"
          />
        </div>
        {__t('influencers_agency_overview.influencers_table.overdue_tasks', { count: overdueTasks.length })}
      </div>
    );

    const overdueTasksByCampaignId = getOverdueTasksByCampaignId(overdueTasks);

    return (
      <Popover
        triggerComponent={content}
        classNameWrapper="influencers-agency-overview-table__overdue-deliverables-popover-wrapper"
        classNamePopover="influencers-agency-overview-table__overdue-deliverables-popover-popover"
      >
        <div
          className="influencers-agency-overview-table__overdue-deliverables-popover-content"
        >
          {
            Object.keys(overdueTasksByCampaignId).map(campaignId => {
              const campaignOverdueTasks = overdueTasksByCampaignId[campaignId];

              return (
                <div
                  key={campaignId}
                  className="influencers-agency-overview-table__overdue-deliverables-campaign-content"
                >
                  <div className="influencers-agency-overview-table__overdue-deliverables-popover-campaign-name">
                    {campaignOverdueTasks[0].campaignName}
                  </div>
                  {
                    campaignOverdueTasks.map((overdueTask: InfluencersAgencyOverdueTask) => {
                      const count = {};

                      return renderContentDeliverable(overdueTask, count);
                    })
                  }
                </div>
              );
            })
          }
        </div>
      </Popover>
    );
  }
});

export const getContextMenuColumn = (deleteProfile: (profileId: number) => void) => ({
  title: '',
  key: 'context_menu',
  width: 40,
  render: (item: AgencyInfluencersProfileOverview) => {
    const MoreButtonWithMenu = withContextMenu(MoreButton, {
      position: 'bottom'
    });

    const items = [
      {
        text: __t('influencers_agency_overview.influencers_table.context_menu.view_campaigns'),
        link: `/organizations/${item.influencerOrganizationId}/campaigns`,
        openInNewTab: true
      },
      {
        text: __t('influencers_agency_overview.influencers_table.context_menu.edit_profile'),
        link: `/content-creator/profiles/${item.id}/edit`,
        openInNewTab: true
      },
      {
        text: __t('influencers_agency_overview.influencers_table.context_menu.view_profile'),
        link: `/content-creator/profiles/${item.id}`,
        openInNewTab: true
      },
      {
        text: __t('influencers_agency_overview.influencers_table.context_menu.delete_account'),
        onClick: () => deleteProfile(item.id)
      }
    ];

    return (
      <MoreButtonWithMenu
        width={20}
        contextMenuProps={{ items }}
      />
    );
  }
});
